import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user } from '@stores/authStore';
import { api } from '@services/api';
import { getStatusLabel, getPriorityLabel } from '@utils/labels';

interface Issue {
  id: number;
  project_code: string;
  issue_number: string;
  title: string;
  status: string;
  priority: string;
  reporter_name: string;
  assignee_name: string;
  created_at: string;
  recent_comments_count?: number;
  is_hidden?: boolean;
}

interface Project {
  id: number;
  code: string;
  name: string;
}

interface User {
  id: number;
  name: string;
  email: string;
}

export function IssuesPage() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectMembers, setProjectMembers] = useState<User[]>([]);
  const [userRole, setUserRole] = useState<string>('');

  // Form state
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [assigneeId, setAssigneeId] = useState<string>('');

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }

    loadIssues();
    loadProjects();
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    if (selectedProjectId) {
      loadProjectMembers(parseInt(selectedProjectId));
    }
  }, [selectedProjectId]);

  const loadIssues = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (priorityFilter) params.append('priority', priorityFilter);

    const response = await api.get<any>(`/api/issues?${params.toString()}`);
    if (response.success && response.data) {
      setIssues(response.data.items || []);
    }
    setLoading(false);
  };

  const loadProjects = async () => {
    const response = await api.get<any>('/api/projects');
    if (response.success && response.data) {
      setProjects(response.data.items || []);
    }
  };

  const loadProjectMembers = async (projectId: number) => {
    const response = await api.get<any>(`/api/projects/${projectId}/members`);
    if (response.success && response.data) {
      const members = response.data.members || [];
      setProjectMembers(members);

      // Get current user's role
      const currentMember = members.find((m: any) => m.id === user.value?.id);
      if (currentMember) {
        setUserRole(currentMember.role);
      } else {
        setUserRole('');
      }
    }
  };

  const handleCreateIssue = async (e: Event) => {
    e.preventDefault();

    if (!selectedProjectId || !title.trim()) {
      alert('필수 항목을 입력해주세요');
      return;
    }

    const payload = {
      project_id: parseInt(selectedProjectId),
      title: title.trim(),
      description: description.trim(),
      priority,
      assignee_id: assigneeId ? parseInt(assigneeId) : null,
    };

    const response = await api.post<any>('/api/issues', payload);
    if (response.success) {
      setShowCreateModal(false);
      setSelectedProjectId('');
      setTitle('');
      setDescription('');
      setPriority('medium');
      setAssigneeId('');
      loadIssues();
    } else {
      alert(response.error || '이슈 생성 실패');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      review: 'bg-purple-100 text-purple-800',
      on_hold: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-600 text-white font-semibold',
      resolved: 'bg-green-100 text-green-800',
      updated: 'bg-cyan-100 text-cyan-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100 text-gray-600';
  };

  if (!isAuthenticated.value) return null;

  return (
    <div class="min-h-screen bg-gray-100">
      <header class="bg-white shadow">
        <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div class="flex items-center gap-3">
            <img src="/logo.svg" alt="DbitLabs Logo" class="h-8 w-8" />
            <h1 class="text-xl font-bold">이슈 목록</h1>
          </div>
          <div class="flex gap-2">
            <button onClick={() => setShowCreateModal(true)} class="btn btn-primary">
              이슈 생성
            </button>
            <button onClick={() => route('/dashboard')} class="btn btn-secondary">
              대시보드로 돌아가기
            </button>
          </div>
        </div>
      </header>

      <main class="mx-auto max-w-7xl px-4 py-8">
        <div class="mb-6 flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}
            class="input"
          >
            <option value="">전체 상태</option>
            <option value="rejected">거절됨</option>
            <option value="new">신규</option>
            <option value="review">검토중</option>
            <option value="in_progress">진행중</option>
            <option value="on_hold">보류됨</option>
            <option value="resolved">해결됨</option>
            <option value="updated">업데이트완료</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter((e.target as HTMLSelectElement).value)}
            class="input"
          >
            <option value="">전체 우선순위</option>
            <option value="low">낮음</option>
            <option value="medium">보통</option>
            <option value="high">높음</option>
            <option value="critical">긴급</option>
          </select>
        </div>

        {loading ? (
          <div class="text-center">로딩중...</div>
        ) : (
          <div class="card">
            <table class="w-full">
              <thead>
                <tr class="border-b">
                  <th class="py-3 text-left">이슈 번호</th>
                  <th class="py-3 text-left">제목</th>
                  <th class="py-3 text-left">상태</th>
                  <th class="py-3 text-left">우선순위</th>
                  <th class="py-3 text-left">담당자</th>
                  <th class="py-3 text-left">생성일</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr
                    key={issue.id}
                    onClick={() => route(`/issues/${issue.id}`)}
                    class="cursor-pointer border-b hover:bg-gray-50"
                  >
                    <td class="py-3">
                      <span class="font-mono text-sm">{issue.issue_number}</span>
                    </td>
                    <td class="py-3">
                      {issue.is_hidden && (
                        <svg class="inline-block w-4 h-4 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                        </svg>
                      )}
                      {issue.title}
                      {(issue.recent_comments_count ?? 0) > 0 && (
                        <span class="ml-2 inline-flex items-center gap-1 rounded bg-blue-500 px-2 py-1 text-xs font-semibold text-white">
                          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z"/>
                          </svg>
                          {issue.recent_comments_count}
                        </span>
                      )}
                    </td>
                    <td class="py-3">
                      <span class={`rounded px-2 py-1 text-xs ${getStatusBadge(issue.status)}`}>
                        {getStatusLabel(issue.status)}
                      </span>
                    </td>
                    <td class="py-3">
                      <span
                        class={`rounded px-2 py-1 text-xs ${getPriorityBadge(issue.priority)}`}
                      >
                        {getPriorityLabel(issue.priority)}
                      </span>
                    </td>
                    <td class="py-3 text-sm">{issue.assignee_name || '미할당'}</td>
                    <td class="py-3 text-sm text-gray-600">
                      {new Date(issue.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {issues.length === 0 && (
              <div class="py-8 text-center text-gray-500">이슈가 없습니다</div>
            )}
          </div>
        )}
      </main>

      {/* Create Issue Modal */}
      {showCreateModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold">새 이슈 생성</h2>
            <form onSubmit={handleCreateIssue}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">프로젝트 *</label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                  required
                >
                  <option value="">프로젝트 선택</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.code} - {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">제목 *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  required
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">설명</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={4}
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">우선순위</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="critical">긴급</option>
                </select>
              </div>

              {userRole !== 'qa' && (
                <div class="mb-6">
                  <label class="mb-1 block text-sm font-medium">담당자</label>
                  <select
                    value={assigneeId}
                    onChange={(e) => setAssigneeId((e.target as HTMLSelectElement).value)}
                    class="input w-full"
                    disabled={!selectedProjectId}
                  >
                    <option value="">미할당</option>
                    {projectMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  이슈 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
